import { itemsAtom } from "@/atoms";
import { Image } from "@/components/image";
import { NoImage } from "@/components/no-image/NoImage";
import OptionPrice from "@/components/item/OptionPrice";
import PromotionalRibbon from "@/components/PromotionalRibbon";
import { useMediaQuery } from "@/hooks/use-media-query";
import { cn } from "@/lib/cn";
import { isOptionAvailable } from "@/lib/is-option-available";
import { Item, OptionGroup } from "@/types";
import { atom, useAtomValue } from "jotai";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { OptionGroupRules } from "@/components/item/OptionGroupRules";
import { Badge, PageContainer, SearchField, SectionHeader, Skeleton, Surface, Text } from "@/components/ui";

/**
 * Read-only option-group card for the indoor info-only menu modal. Visually
 * identical to delivery OptionGroupCard without the required badge or
 * radio/quantity (no cart). OptionPrice shows base price with "+".
 */
export function OptionGroupView({ optionGroup }: { optionGroup: OptionGroup }) {
  const itemOptionsMapAtom = useMemo(
    () =>
      atom<Record<string, Item>>((get) => {
        const items = get(itemsAtom);
        return optionGroup.options.reduce(
          (acc, option) => {
            const item = items.find((i) => i.id === option.itemId);
            if (item) acc[option.id] = item;
            return acc;
          },
          {} as Record<string, Item>,
        );
      }),
    [optionGroup],
  );

  const itemOptionsMap = useAtomValue(itemOptionsMapAtom);
  const isDesktop = useMediaQuery("(min-width: 768px)");

  const shouldShowSearch = useMemo(() => {
    const count = optionGroup.options.length;
    return isDesktop ? count >= 10 : count >= 5;
  }, [optionGroup.options.length, isDesktop]);

  const [filterValue, setFilterValue] = useState("");
  const [filteredItemOptions, setFilteredItemOptions] = useState<Item[]>([]);

  useEffect(() => {
    const values = Object.values(itemOptionsMap);
    if (!filterValue.length) {
      setFilteredItemOptions(values);
    } else {
      setFilteredItemOptions(
        values.filter((i) => i.name.toUpperCase().includes(filterValue.toUpperCase())),
      );
    }
  }, [filterValue, itemOptionsMap]);

  function filterItemOptions(e: ChangeEvent<HTMLInputElement>) {
    setFilterValue(e.target.value);
  }
  function clearFilter() {
    setFilterValue("");
  }

  if (!optionGroup.options?.length) return null;

  return (
    <div>
      <Surface surface="page" className="relative flex justify-between gap-4 overflow-hidden py-4">
        <PromotionalRibbon
          isPromotional={optionGroup.isPromotional}
          text={optionGroup.promotionalBannerText}
        />
        <PageContainer className="flex flex-col items-start gap-2">
          <SectionHeader title={optionGroup.name} spacing="none" className="w-full" />
          <OptionGroupRules optionGroup={optionGroup} />
          {shouldShowSearch && (
            <SearchField
              placeholder="Digite para buscar"
              aria-label="Buscar opções"
              value={filterValue}
              onChange={filterItemOptions}
              onClear={clearFilter}
            />
          )}
        </PageContainer>
      </Surface>

      <PageContainer surface="card" className="flex flex-col">
        {filteredItemOptions.map((item) => {
          const option = optionGroup.options.find((op) => op.itemId === item.id);
          if (!option) return null;
          const unavailable = !isOptionAvailable(option, item);
          return (
            <div
              className={cn(
                "relative w-full overflow-hidden border-b border-border py-4 last:border-b-0",
                unavailable && "opacity-60",
              )}
              key={item.id}
              data-unavailable={unavailable}
            >
              <div className="flex w-full items-center gap-3">
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  {item.image && (
                    <div className="relative h-24 w-24 shrink-0 overflow-hidden rounded-control md:h-28 md:w-28">
                      <PromotionalRibbon
                        isPromotional={option.isPromotional}
                        text={option.promotionalBannerText}
                        size="sm"
                      />
                      {item.image.URL ? (
                        <>
                          <Skeleton className="absolute inset-0 h-full" />
                          <Image
                          width={200}
                          height={200}
                          alt={item.name}
                          src={item.image.URL}
                          crc={item.image["CRC-32"]}
                          sizes="(min-width: 768px) 112px, 96px"
                          className="relative h-full w-full object-cover"
                          />
                        </>
                      ) : (
                        <NoImage
                          width={200}
                          height={200}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                  )}
                  <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <Text as="span" weight="bold" clamp={2} className={cn(unavailable && "line-through")}>
                      {item.name}
                    </Text>
                    <Text as="span" size="sm" tone="muted" clamp={2}>
                      {item.description}
                    </Text>
                    <OptionPrice
                      price={option.price}
                      quantity={0}
                      priceMethod={optionGroup.priceMethod}
                    />
                  </div>
                </div>
                {unavailable ? (
                  <Badge variant="outline" tone="neutral" size="md">
                    Indisponível
                  </Badge>
                ) : (
                  <div className="h-11 w-11 shrink-0" aria-hidden />
                )}
              </div>
            </div>
          );
        })}
      </PageContainer>
    </div>
  );
}
